import React, { useMemo, useState } from 'react'; // Adicionado useMemo para performance
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
// ... outros imports mantidos
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import AssessmentIcon from '@mui/icons-material/Assessment';
import LogoutIcon from '@mui/icons-material/Logout';
import InventoryIcon from '@mui/icons-material/Inventory';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import SettingsIcon from '@mui/icons-material/Settings';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import GridOnIcon from '@mui/icons-material/GridOn';
import MapIcon from '@mui/icons-material/Map';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount'; // Novo ícone sugerido
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import AssignmentLateIcon from '@mui/icons-material/AssignmentLate';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import DashboardIcon from '@mui/icons-material/Dashboard';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import styles from './DashboardLayout.module.css';
import { useAuthStore, AuthState } from '../stores/authStore';
import UploadProgressToast from '../components/UploadProgressToast/UploadProgressToast';
import { useThemeByEmpresa } from '../hooks/useThemeByEmpresa';
import SettingsModal from '../components/SettingsModal/SettingsModal';

const DashboardLayout: React.FC = () => {
    const logout = useAuthStore((state: AuthState) => state.logout);
    const user = useAuthStore((state: AuthState) => state.user);
    const navigate = useNavigate();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);

    // Carrega o tema da empresa e obtém o nome da empresa
    const { empresa, themeMode, toggleThemeMode, themeShape, toggleThemeShape } = useThemeByEmpresa();

    const logoSrc = empresa === 'IMBISEG'
        ? (themeMode === 'dark' ? '/logo-imbiseg-branca.png' : '/logo-imbiseg.png')
        : '/Logo.webp';

    const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);
    const closeMobileMenu = () => setIsMobileMenuOpen(false);

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const isAdmin = user?.role === 'admin';
    // Define a role de supervisor/diretor/admin, que tem acesso a Aprovação e Equipe
    const isSupervisorLevel = user?.role === 'supervisor' || user?.role === 'diretor' || isAdmin;
    const canSeeAmostras = user?.usuarioAmostras === 1;
    const isAws = user?.isAws === true;

    // A lista de navegação agora é dinâmica e memorizada
    const navItems = useMemo(() => {
        if (isAws) {
            return [
                { to: "/aws-pedidos", label: "Pedidos", icon: <ReceiptLongIcon /> },
                { to: "/clientes", label: "Clientes", icon: <PeopleAltIcon /> },
                { to: "/aws-produtos", label: "Produtos", icon: <InventoryIcon /> },
                { to: "/amostras-pedidos", label: "Registro Amostras", icon: <FactCheckIcon /> },
            ];
        }

        return [
            // --- Nível Estratégico / Analítico ---
            { to: "/", label: "Dashboard", icon: <LeaderboardIcon />, end: true },
            { to: "/xadrez-vendas", label: "Xadrez de Vendas", icon: <GridOnIcon /> },
            { to: '/vendas-regiao', label: 'Vendas por Região', icon: <MapIcon /> },
            ...(isSupervisorLevel ? [
                { to: '/vendedores', label: 'Equipe', icon: <AssessmentIcon /> }
            ] : []),

            // --- Nível Operacional / Comercial ---
            ...(isSupervisorLevel ? [
                { to: '/aprovacao-pedidos', label: 'Aprovação Pedidos', icon: <FactCheckIcon /> }
            ] : []),
            { to: "/pedidos", label: "Pedidos", icon: <ReceiptLongIcon /> },
            { to: "/rastreio", label: "Rastreio", icon: <LocalShippingIcon /> },
            { to: "/clientes", label: "Clientes", icon: <PeopleAltIcon /> },
            { to: "/produtos", label: "Produtos", icon: <InventoryIcon /> },

            // --- Pós-Venda / Suporte / Amostras ---
            { to: "/reclamacoes", label: "Reclamações", icon: <AssignmentLateIcon /> },
            ...(canSeeAmostras ? [
                { to: "/amostras-pedidos", label: "Registro Amostras", icon: <FactCheckIcon /> },
                { to: "/amostras-dashboard", label: "Dashboard Amostras", icon: <DashboardIcon /> },
            ] : []),
        ];
    }, [isSupervisorLevel, canSeeAmostras, isAws]); // Recalcula apenas se o status mudar

    return (<div className="zoom-wrapper">
        <div className={styles.layoutContainer}>
            {/* Botão Hambúrguer para Mobile */}
            <button className={styles.mobileMenuButton} onClick={toggleMobileMenu}>
                {isMobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
            </button>

            {/* Overlay para fechar o menu ao clicar fora no mobile */}
            {isMobileMenuOpen && <div className={styles.mobileOverlay} onClick={closeMobileMenu} />}

            <aside className={`${styles.sidebar} ${isMobileMenuOpen ? styles.sidebarOpen : ''}`}>
                <div className={styles.sidebarTop}>
                    <div className={styles.logoContainer}>
                        <img src={logoSrc} alt="Logo da Empresa" className={styles.logo} />
                        {empresa !== 'IMBISEG' && (
                            <img src={logoSrc} alt="Logo da Empresa" className={styles.logonome} />
                        )}
                    </div>

                    <nav>
                        <ul className={styles.navList}>
                            {navItems.map((item) => (
                                <li key={item.to}>
                                    <NavLink
                                        to={item.to}
                                        end={item.end}
                                        onClick={closeMobileMenu}
                                        className={({ isActive }) =>
                                            `${styles.navLink} ${isActive ? styles.navLinkActive : ''}`
                                        }
                                    >
                                        {item.icon}
                                        <span className={styles.navLabel}>{item.label}</span>
                                    </NavLink>
                                </li>
                            ))}
                        </ul>
                    </nav>
                </div>

                <div className={styles.sidebarBottom}>
                    {/* Botão de Configurações (Engrenagem) */}
                    <button
                        onClick={() => { setIsSettingsOpen(true); closeMobileMenu(); }}
                        className={styles.logoutButton}
                        title="Configurações & Perfil"
                    >
                        <SettingsIcon />
                        <span className={styles.navLabel}>Configurações</span>
                    </button>

                    {/* Admin Link */}
                    {isAdmin && (
                        <NavLink
                            to="/admin"
                            onClick={closeMobileMenu}
                            className={({ isActive }) =>
                                `${styles.navLink} ${isActive ? styles.navLinkActive : ''}`
                            }
                        >
                            <ManageAccountsIcon />
                            <span className={styles.navLabel}>Admin</span>
                        </NavLink>
                    )}

                    {/* Botão de Sair */}
                    <button onClick={() => { handleLogout(); closeMobileMenu(); }} className={styles.logoutButton}>
                        <LogoutIcon />
                        <span className={styles.navLabel}>Sair</span>
                    </button>
                </div>
            </aside>

            <main className={styles.content}>
                <Outlet />
            </main>
        </div>
        <UploadProgressToast />
        <SettingsModal
            isOpen={isSettingsOpen}
            onClose={() => setIsSettingsOpen(false)}
            themeMode={themeMode}
            onToggleThemeMode={toggleThemeMode}
            themeShape={themeShape}
            onToggleThemeShape={toggleThemeShape}
        />
    </div>
    );
};

export default DashboardLayout;